import { useState, useEffect } from 'react';
import {
  Card, Row, Col, Select, Button, Table, Space, message, Tag, Tooltip, Alert
} from 'antd';
import {
  SearchOutlined, FilePdfOutlined, FileExcelOutlined, TableOutlined
} from '@ant-design/icons';
import apiClient from '../../../api/apiClient';
import { getCicloActual, getAniosAcademicos } from '../../../utils/cicloEscolar';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Option } = Select;

const NOTA_MINIMA = 60;

// Color de celda según promedio
const colorNota = (val) => {
  if (val === null || val === undefined) return {};
  return val >= NOTA_MINIMA
    ? { backgroundColor: '#f6ffed', color: '#389e0d', fontWeight: 600 }
    : { backgroundColor: '#fff1f0', color: '#cf1322', fontWeight: 600 };
};

// Props por defecto = Listado de Cotejo. "Cuadro de Promedios" reutiliza esta pantalla
// con el endpoint de orden fijo por áreas y sin selector de ciclo.
const ListadoCotejo = ({
  titulo = 'Listado de Cotejo de Alumnos',
  endpoint = '/boleta-calificaciones/cuadro-cotejo',
  archivo = 'Cotejo',
  conCiclo = true,
}) => {
  const [filtros, setFiltros] = useState({
    cicloEscolar: getCicloActual(),
    idGrado: null,
    idSeccion: null,
    idJornada: null,
  });

  const [grados, setGrados] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [jornadas, setJornadas] = useState([]);
  const [loadingCatalogos, setLoadingCatalogos] = useState(true);

  const [cursos, setCursos] = useState([]);
  const [alumnos, setAlumnos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buscado, setBuscado] = useState(false);   // si ya se hizo al menos una búsqueda

  useEffect(() => {
    const cargarCatalogos = async () => {
      try {
        const [gradosRes, seccionesRes, jornadasRes] = await Promise.all([
          apiClient.get('/grados'),
          apiClient.get('/secciones'),
          apiClient.get('/jornadas'),
        ]);
        if (gradosRes.data.success)    setGrados(gradosRes.data.data);
        if (seccionesRes.data.success) setSecciones(seccionesRes.data.data);
        if (jornadasRes.data.success)  setJornadas(jornadasRes.data.data);
      } catch {
        message.error('Error al cargar los catálogos');
      } finally {
        setLoadingCatalogos(false);
      }
    };
    cargarCatalogos();
  }, []);

  const filtrosCompletos =
    filtros.cicloEscolar && filtros.idGrado && filtros.idSeccion && filtros.idJornada;

  const buscar = async () => {
    if (!filtrosCompletos) {
      message.warning('Seleccioná grado, sección y jornada para buscar');
      return;
    }
    setLoading(true);
    setBuscado(true);
    try {
      const res = await apiClient.get(endpoint, {
        params: {
          cicloEscolar: filtros.cicloEscolar,
          idGrado:      filtros.idGrado,
          idSeccion:    filtros.idSeccion,
          idJornada:    filtros.idJornada,
        },
      });
      if (res.data.success) {
        setCursos(res.data.data.cursos  || []);
        setAlumnos(res.data.data.alumnos || []);
      } else {
        message.error(`Error al obtener el ${titulo.toLowerCase()}`);
      }
    } catch {
      message.error('Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  // ── Etiqueta del salón actual ──────────────────────────────────────────────
  const etiquetaSalon = () => {
    if (!buscado || !filtros.idGrado) return '';
    const g = grados.find(x => x.IdGrado     === filtros.idGrado);
    const s = secciones.find(x => x.IdSeccion === filtros.idSeccion);
    const j = jornadas.find(x => x.IdJornada  === filtros.idJornada);
    return [
      filtros.cicloEscolar,
      g?.NombreGrado,
      s ? `Sección ${s.NombreSeccion}` : '',
      j?.NombreJornada,
    ].filter(Boolean).join(' · ');
  };

  // ── Columnas dinámicas para Ant Design Table ──────────────────────────────
  const columnas = [
    {
      title: '#',
      key: 'num',
      width: 48,
      fixed: 'left',
      align: 'center',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Apellidos y Nombres',
      key: 'alumno',
      width: 220,
      fixed: 'left',
      render: (_, record) => (
        <span style={{ fontWeight: 500 }}>
          {record.Apellidos}, {record.Nombres}
        </span>
      ),
      sorter: (a, b) => a.Apellidos.localeCompare(b.Apellidos),
    },
    ...cursos.map((curso, colIdx) => ({
      title: (
        <Tooltip title={curso.NombreCurso}>
          <span style={{ fontSize: 11 }}>{curso.NombreCurso}</span>
        </Tooltip>
      ),
      key: `curso_${curso.idCurso}`,
      width: 90,
      align: 'center',
      render: (_, record) => {
        const val = record.promedios[colIdx]?.promedio ?? null;
        if (val === null) return <span style={{ color: '#bfbfbf' }}>—</span>;
        return (
          <span style={{ ...colorNota(val), display: 'inline-block', width: '100%' }}>
            {val}
          </span>
        );
      },
      sorter: (a, b) => {
        const va = a.promedios[colIdx]?.promedio ?? -1;
        const vb = b.promedios[colIdx]?.promedio ?? -1;
        return va - vb;
      },
    })),
  ];

  // ── Exportar PDF (landscape) ───────────────────────────────────────────────
  const exportarPDF = () => {
    if (!alumnos.length) { message.warning('No hay datos para exportar'); return; }

    const doc = new jsPDF({ orientation: 'landscape' });
    const pageW = doc.internal.pageSize.getWidth();
    let y = 14;

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(titulo.toUpperCase(), pageW / 2, y, { align: 'center' });
    y += 7;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(etiquetaSalon(), pageW / 2, y, { align: 'center' });
    y += 5;

    const g = grados.find(x => x.IdGrado     === filtros.idGrado);
    const s = secciones.find(x => x.IdSeccion  === filtros.idSeccion);
    const j = jornadas.find(x => x.IdJornada   === filtros.idJornada);
    const fechaReporte = new Date().toLocaleDateString('es-GT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });

    doc.setFontSize(8);
    doc.text(
      `Grado: ${g?.NombreGrado || ''}   Sección: ${s?.NombreSeccion || ''}   Jornada: ${j?.NombreJornada || ''}   Ciclo: ${filtros.cicloEscolar}   Fecha: ${fechaReporte}`,
      pageW / 2, y, { align: 'center' }
    );
    y += 3;

    doc.setDrawColor(200);
    doc.line(14, y, pageW - 14, y);
    y += 4;

    // Tabla
    const encabezados = ['#', 'Apellidos y Nombres',...cursos.map(c => c.NombreCurso)];
    const filas = alumnos.map((alumno, i) => [
      i + 1,
      `${alumno.Apellidos}, ${alumno.Nombres}`,
      ...alumno.promedios.map(p => (p.promedio !== null && p.promedio !== undefined ? p.promedio : '—')),
    ]);

    autoTable(doc, {
      startY: y,
      head: [encabezados],
      body: filas,
      styles: { fontSize: 7, cellPadding: 2, halign: 'center' },
      headStyles: { fillColor: [24, 144, 255], textColor: 255, fontStyle: 'bold', fontSize: 7 },
      columnStyles: {
        0: { cellWidth: 8 },
        1: { halign: 'left', cellWidth: 50 },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index >= 2) {
          const val = data.cell.text[0];
          if (val !== '—') {
            const n = Number(val);
            if (!isNaN(n)) {
              data.cell.styles.textColor = n >= NOTA_MINIMA ? [40, 140, 40] : [180, 30, 30];
              data.cell.styles.fontStyle = 'bold';
            }
          }
        }
      },
      alternateRowStyles: { fillColor: [245, 247, 250] },
    });

    const apellidos = (g?.NombreGrado || 'grado').replace(/\s+/g, '_');
    doc.save(`${archivo}_${apellidos}_${filtros.cicloEscolar}.pdf`);
    message.success('PDF generado correctamente');
  };

  // ── Exportar Excel ─────────────────────────────────────────────────────────
  const exportarExcel = () => {
    if (!alumnos.length) { message.warning('No hay datos para exportar'); return; }

    const g = grados.find(x => x.IdGrado     === filtros.idGrado);
    const s = secciones.find(x => x.IdSeccion  === filtros.idSeccion);
    const j = jornadas.find(x => x.IdJornada   === filtros.idJornada);
    const fechaReporte = new Date().toLocaleDateString('es-GT');

    const metaRows = [
      [titulo.toUpperCase()],
      [],
      ['Ciclo Escolar:',  filtros.cicloEscolar],
      ['Grado:',          g?.NombreGrado  || ''],
      ['Sección:',        s?.NombreSeccion || ''],
      ['Jornada:',        j?.NombreJornada || ''],
      ['Fecha reporte:',  fechaReporte],
      ['Total alumnos:',  alumnos.length],
      [],
      // Encabezados de la tabla
      ['#', 'Apellidos', 'Nombres', ...cursos.map(c => c.NombreCurso)],
      // Filas de datos
      ...alumnos.map((alumno, i) => [
        i + 1,
        alumno.Apellidos,
        alumno.Nombres,
        ...alumno.promedios.map(p =>
          p.promedio !== null && p.promedio !== undefined ? p.promedio : ''
        ),
      ]),
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(metaRows);
    XLSX.utils.book_append_sheet(wb, ws, archivo);
    saveAs(
      new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })]),
      `${archivo}_${(g?.NombreGrado || 'grado').replace(/\s+/g, '_')}_${filtros.cicloEscolar}.xlsx`
    );
    message.success('Excel exportado correctamente');
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const hayDatos = buscado && alumnos.length > 0;

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <TableOutlined />
            {titulo}
          </Space>
        }
        extra={
          hayDatos && (
            <Space>
              <Button icon={<FilePdfOutlined />} onClick={exportarPDF}>
                PDF
              </Button>
              <Button icon={<FileExcelOutlined />} onClick={exportarExcel} style={{ borderColor: '#52c41a', color: '#389e0d' }}>
                Excel
              </Button>
            </Space>
          )
        }
      >
        {/* Filtros */}
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          {conCiclo && <Col xs={24} sm={12} md={5} lg={4}>
            <Select
              placeholder="Ciclo escolar"
              style={{ width: '100%' }}
              value={filtros.cicloEscolar}
              onChange={val => setFiltros(p => ({ ...p, cicloEscolar: val }))}
            >
              {getAniosAcademicos().map(y => (
                <Option key={y} value={y}>{y}</Option>
              ))}
            </Select>
          </Col>}

          <Col xs={24} sm={12} md={6} lg={5}>
            <Select
              placeholder="Grado *"
              style={{ width: '100%' }}
              value={filtros.idGrado}
              onChange={val => setFiltros(p => ({ ...p, idGrado: val }))}
              loading={loadingCatalogos}
              showSearch
              optionFilterProp="children"
              status={buscado && !filtros.idGrado ? 'error' : ''}
            >
              {grados.map(g => (
                <Option key={g.IdGrado} value={g.IdGrado}>{g.NombreGrado}</Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={4} lg={4}>
            <Select
              placeholder="Sección *"
              style={{ width: '100%' }}
              value={filtros.idSeccion}
              onChange={val => setFiltros(p => ({ ...p, idSeccion: val }))}
              loading={loadingCatalogos}
              status={buscado && !filtros.idSeccion ? 'error' : ''}
            >
              {secciones.map(s => (
                <Option key={s.IdSeccion} value={s.IdSeccion}>{s.NombreSeccion}</Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={5} lg={4}>
            <Select
              placeholder="Jornada *"
              style={{ width: '100%' }}
              value={filtros.idJornada}
              onChange={val => setFiltros(p => ({ ...p, idJornada: val }))}
              loading={loadingCatalogos}
              status={buscado && !filtros.idJornada ? 'error' : ''}
            >
              {jornadas.map(j => (
                <Option key={j.IdJornada} value={j.IdJornada}>{j.NombreJornada}</Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={4} lg={3}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={buscar}
              loading={loading}
              disabled={!filtrosCompletos}
              style={{ width: '100%' }}
            >
              Buscar
            </Button>
          </Col>
        </Row>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Todos los filtros son obligatorios. El promedio se calcula dividiendo siempre entre 4 unidades, coincidiendo con la boleta individual."
        />

        {/* Leyenda de colores */}
        {hayDatos && (
          <Space style={{ marginBottom: 12 }}>
            <Tag color="success">≥ 60 — Aprueba</Tag>
            <Tag color="error">&lt; 60 — Reprueba</Tag>
            <Tag color="default">— Sin notas</Tag>
            <span style={{ color: '#888', fontSize: 12 }}>
              {alumnos.length} alumno{alumnos.length !== 1 ? 's' : ''} · {cursos.length} curso{cursos.length !== 1 ? 's' : ''}
            </span>
          </Space>
        )}

        {/* Tabla */}
        <div style={{ overflowX: 'auto' }}>
          <Table
            columns={columnas}
            dataSource={alumnos}
            rowKey="IdAlumno"
            loading={loading}
            size="small"
            bordered
            pagination={{
              pageSize: 40,
              showSizeChanger: true,
              pageSizeOptions: ['20', '40', '60', '100'],
              showTotal: total => `Total: ${total} alumnos`,
            }}
            scroll={{ x: 220 + cursos.length * 90 }}
            locale={{
              emptyText: buscado
                ? 'No se encontraron alumnos con esos filtros'
                : 'Seleccioná los filtros y presioná Buscar',
            }}
          />
        </div>
      </Card>
    </div>
  );
};

export default ListadoCotejo;
